import { useEffect, useMemo, useRef, useState } from "react";
import "../../styles/common/select.css";
import { ReactComponent as DownArrow } from "../../assets/icons/down_arrow.svg";
import tick from "../../assets/icons/tick.svg";

function Select({
  options = [],
  optionMap,
  value,
  setValue,
  placeholder = "Select",
  itemName = { singular: "item", plural: "items" },
  multiSelect = false,
  disabled = false,
  fullWidth = false,
  width = 600,
}) {
  const swidth = window.innerWidth;

  const selectRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [filterText, setFilterText] = useState("");

  const selectedValues = useMemo(() => {
    if (multiSelect) return Array.isArray(value) ? value : [];
    return value ? [value] : [];
  }, [value, multiSelect]);

  const filteredOptions = useMemo(() => {
    if (!filterText) return options;
    return options.filter((opt) =>
      opt.toLowerCase().includes(filterText.toLowerCase())
    );
  }, [filterText, options]);

  const displayPlaceholder = useMemo(() => {
    if (!multiSelect) {
      if (selectedValues.length === 0) {
        return placeholder;
      } else if (optionMap) {
        return optionMap[selectedValues[0]];
      }
      return selectedValues[0];
    }

    const count = selectedValues.length;
    if (count === 0) return placeholder;

    return `${count} ${
      count === 1 ? itemName.singular : itemName.plural
    } selected`;
  }, [selectedValues, multiSelect, placeholder, itemName, optionMap]);

  const handleSelectClick = () => {
    if (!disabled || !isOpen) setIsOpen(true);
  };

  const toggleOption = (option) => {
    if (multiSelect) {
      const updated = selectedValues.includes(option)
        ? selectedValues.filter((v) => v !== option)
        : [...selectedValues, option];

      setValue(updated);
    } else {
      setValue(option);
    }

    setFilterText("");
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (selectRef.current && !selectRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const renderIcon = (option) => {
    if (multiSelect) {
      return selectedValues.includes(option) ? (
        <img src={tick} alt="selected" />
      ) : (
        <div className="circle" />
      );
    }

    return selectedValues[0] === option ? (
      <div className="circle flex-jcc-aic">
        <div className="inner-circle" />
      </div>
    ) : (
      <div className="circle" />
    );
  };

  return (
    <div
      className="select-container"
      ref={selectRef}
      style={{ width: swidth <= width || fullWidth ? "100%" : `${width}px` }}
    >
      <div
        className={`select-element flex-jcsb-aic ${isOpen ? "selected" : ""}`}
        onClick={handleSelectClick}
      >
        <input
          className="select-value"
          placeholder={displayPlaceholder}
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          disabled={disabled}
        />
        <div className="down-arrow flex-jcc-aic">
          <DownArrow alt="down_arrow" />
        </div>
      </div>
      {isOpen && filteredOptions.length > 0 && (
        <div className="options">
          {filteredOptions.map((option) => (
            <div
              key={option}
              className="option"
              onClick={() => toggleOption(option)}
            >
              {renderIcon(option)}
              <p>{optionMap ? optionMap[option] : option}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Select;
